import { isValidElement } from "react";
import { createSlice, nanoid } from "@reduxjs/toolkit";

export const MAX_VISIBLE = 3;
export const FALLBACK_ERROR = "Something went wrong. Please try again.";

const SEVERITIES = ["success", "error", "warning", "info"];
const ALIASES = { danger: "error", warn: "warning" };
const BASE_DURATION = { success: 4000, info: 4000, warning: 6000, error: 6000 };
const MAX_DURATION = 10000;
// setTimeout treats anything above this (Infinity included) as 0.
const MAX_TIMEOUT = 2 ** 31 - 1;
const MAX_LENGTH = 300;
const MAX_DEPTH = 8;

/**
 * API errors reach the snackbar in every shape the backend produces: strings,
 * DRF field dicts ({ email: ["…"] }, nested for line items), arrays,
 * Error/AxiosError objects, and the odd JSX fragment. They are flattened to
 * one string here because a raw object rendered by the app-root toast would
 * crash the whole app, not just the page.
 */
export const toMessage = (value, depth = 0, seen = new WeakSet()) => {
  if (value == null || typeof value === "boolean" || depth > MAX_DEPTH) {
    return "";
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : "";
  }
  if (typeof value === "string") {
    // A proxy/server error page, not a message anyone can act on.
    return /^\s*<(!doctype|html)/i.test(value) ? "" : value.trim();
  }
  if (typeof value !== "object" || seen.has(value)) return "";
  seen.add(value);

  const join = (values) =>
    values
      .map((v) => toMessage(v, depth + 1, seen))
      .filter(Boolean)
      .join(" ");

  // Text content only; interactive children belong in `action`.
  if (isValidElement(value)) return join([value.props?.children]);
  if (Array.isArray(value)) return join(value);
  // Errors (AxiosError included) carry request internals as own fields.
  if (value instanceof Error) return toMessage(value.message);
  if (typeof value.message === "string" && value.message.trim()) {
    return toMessage(value.message);
  }
  return join(Object.values(value));
};

// By code point, so an emoji at the cut isn't split into a broken glyph.
const clamp = (text) => {
  const chars = Array.from(text);
  return chars.length > MAX_LENGTH
    ? `${chars.slice(0, MAX_LENGTH - 1).join("").trimEnd()}…`
    : text;
};

// Long enough to read (~50ms per character), never shorter than the
// severity's base or longer than 10s.
const durationFor = (severity, text) =>
  Math.min(
    MAX_DURATION,
    Math.max(BASE_DURATION[severity], 1500 + text.length * 50),
  );

// 0 (or Infinity) keeps the toast until dismissed.
const toDuration = (value, severity, text) => {
  if (value === Infinity) return 0;
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return Math.min(value, MAX_TIMEOUT);
  }
  return durationFor(severity, text);
};

const toSeverity = (value) => {
  const raw = String(value ?? "").toLowerCase();
  if (SEVERITIES.includes(raw)) return raw;
  return ALIASES[raw] || "info";
};

/**
 * Accepts the legacy setSnack payload ({ open, text, severity, title }) and
 * ignores vertical/horizontal: every notification renders in the same place.
 * `duration` (ms) overrides the computed timeout; 0 keeps it until dismissed.
 */
const prepareToast = (payload) => {
  const input =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? payload
      : { text: payload };

  const severity = toSeverity(input.severity);
  const title = clamp(toMessage(input.title));
  let text = clamp(toMessage(input.text));
  // An error with no readable message still has to tell the user it failed.
  if (!text && !title && severity === "error") text = FALLBACK_ERROR;

  // Only the label is state; the handler lives in toastActions (see below).
  const label = toMessage(input.action?.label);

  return {
    payload: {
      id: nanoid(),
      // Legacy callers closed their local toast with open: false; that is a
      // no-op now rather than a toast that pops open.
      open: input.open !== false,
      severity,
      title,
      text,
      ...(label && { action: { label } }),
      duration: toDuration(
        input.duration,
        severity,
        `${title} ${text}`.trim(),
      ),
    },
  };
};

const initialState = { items: [] };

export const snackBarSlice = createSlice({
  name: "snackBar",
  initialState,
  reducers: {
    showSnackBar: {
      reducer: (state, action) => {
        const { open, ...toast } = action.payload;
        if (!open || !(toast.text || toast.title)) return;

        // A repeated message replaces its copy (restarting the timer) instead
        // of stacking identical toasts.
        state.items = state.items.filter(
          (t) =>
            !(
              t.severity === toast.severity &&
              t.title === toast.title &&
              t.text === toast.text
            ),
        );
        state.items.push(toast);

        // Over the limit, drop the oldest non-error first so a burst of
        // successes can't push an unread error off screen. The toast just
        // added is never the one dropped.
        while (state.items.length > MAX_VISIBLE) {
          const last = state.items.length - 1;
          const i = state.items.findIndex(
            (t, idx) => idx < last && t.severity !== "error",
          );
          state.items.splice(i === -1 ? 0 : i, 1);
        }
      },
      prepare: prepareToast,
    },
    // With an id, removes that toast; without one, clears them all.
    hideSnackBar: (state, action) => {
      state.items =
        action.payload == null
          ? []
          : state.items.filter((t) => t.id !== action.payload);
    },
  },
});

export const { showSnackBar, hideSnackBar } = snackBarSlice.actions;

/**
 * Action handlers are functions, so they stay out of Redux (serializable
 * state) and live here keyed by toast id. The Toast looks its handler up and
 * releases it on unmount. Only createNotifier registers one, so a raw
 * dispatch(showSnackBar({ action })) renders no button rather than a dead one.
 */
const toastActions = new Map();
export const getToastAction = (id) => toastActions.get(id);
export const releaseToastAction = (id) => toastActions.delete(id);

/**
 * The notify function returned by useSnackBar() and exported from
 * helpers/notify. Callable with the legacy setSnack shapes, plus
 * notify.success / .error / .warning / .info(text, options).
 * Each call returns the toast id, which notify.dismiss(id) accepts.
 *
 * options.action = { label, onClick } adds a button to the toast.
 */
export const createNotifier = (dispatch) => {
  const show = (payload) => {
    const action = showSnackBar(payload);
    const { id, open, text, title } = action.payload;
    const onClick = payload?.action?.onClick;
    if (action.payload.action && typeof onClick === "function" && open && (text || title)) {
      toastActions.set(id, onClick);
    }
    dispatch(action);
    return id;
  };

  // Functional updaters get {}: there is no per-page state to merge into.
  const notify = (update) =>
    show(typeof update === "function" ? update({}) : update);

  SEVERITIES.forEach((severity) => {
    notify[severity] = (text, options) =>
      show({ ...options, text, severity });
  });
  notify.dismiss = (id) => dispatch(hideSnackBar(id));

  return notify;
};

export default snackBarSlice.reducer;
